// Shared bits for the Scheduler surface (Cronicle jobs and runs).
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getApiErrorCode, getApiErrorField, getApiErrorMessage } from '@/client/utils/errors';
import {
  useRunNowMutation,
  useSetJobEnabledMutation,
} from '@/client/api/scheduler/scheduler.query';
import {
  AuditAction,
  Coverage,
  JobHealth,
  JobTiming,
  SchedulerJob,
  JobWindow,
  SchedulerWindow,
  SeriesPoint,
} from '@/client/types/scheduler';

export { PageShell, StatCard, fmtNum } from '../Integrations/integrationsUi';

export const SCHEDULER_BASE = '/admin/scheduler';

export const WINDOWS: SchedulerWindow[] = ['24h', '3d', '7d'];
export const JOB_WINDOWS: JobWindow[] = ['24h', '3d', '7d', '30d', '90d'];

export const INPUT_CLASS =
  'h-9 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400';

/* ---------- formatting ---------- */

export const fmtTime = (sec?: number | null) => (sec ? new Date(sec * 1000).toLocaleString() : '—');

export const fmtDay = (sec?: number | null) =>
  sec ? new Date(sec * 1000).toLocaleDateString() : '—';

export const fmtAgo = (sec?: number | null) => {
  if (!sec) return '—';
  const diff = Math.max(0, Math.round(Date.now() / 1000 - sec));
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
};

export const fmtDur = (sec?: number | null) => {
  if (sec == null) return '—';
  if (sec < 1) return `${Math.round(sec * 1000)}ms`;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${Math.round(sec % 60)}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

/** Null means nothing ran, so say so rather than showing a perfect or empty rate. */
export const fmtRate = (rate?: number | null) =>
  rate == null ? '—' : `${(rate * 100).toFixed(rate > 0 && rate < 0.1 ? 1 : 0)}%`;

export const isOk = (code: number | string) => Number(code) === 0;

export const fmtBytes = (n?: number | null) =>
  n == null ? '—' : n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`;

/* ---------- schedule in words ---------- */

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const pad = (n: number) => String(n).padStart(2, '0');
const has = (a?: number[]) => Array.isArray(a) && a.length > 0;
const sorted = (a: number[]) => [...a].sort((x, y) => x - y);

/** The step N when `arr` is exactly 0, N, 2N ... across `span`; otherwise 0. */
export const stepOf = (arr: number[], span: number) => {
  const s = sorted(arr);
  if (s.length < 2) return 0;
  const step = s[1] - s[0];
  if (s[0] !== 0 || step < 1 || span % step !== 0 || s.length !== span / step) return 0;
  return s.every((v, i) => v === i * step) ? step : 0;
};

export const describeTiming = (t: false | JobTiming | null | undefined): string => {
  if (!t) return 'On demand';
  const { minutes, hours, days, months, weekdays } = t;
  const dayPart: string[] = [];
  if (has(weekdays))
    dayPart.push(
      sorted(weekdays!)
        .map((d) => DAY_NAMES[d] ?? String(d))
        .join(', '),
    );
  if (has(days)) dayPart.push(`day ${sorted(days!).join(', ')}`);
  const monthPart = has(months)
    ? ` in ${sorted(months!)
        .map((m) => MONTH_NAMES[m - 1] ?? String(m))
        .join(', ')}`
    : '';
  const yearPart = has(t.years) ? ` in ${sorted(t.years!).join(', ')}` : '';
  const tail = `${monthPart}${yearPart}`;

  if (!has(hours)) {
    let base: string;
    if (!has(minutes)) base = 'Every minute';
    else {
      const step = stepOf(minutes!, 60);
      if (step === 1) base = 'Every minute';
      else if (step) base = `Every ${step} min`;
      else
        base = `Hourly at ${sorted(minutes!)
          .map((m) => `:${pad(m)}`)
          .join(', ')}`;
    }
    return `${base}${dayPart.length ? ` on ${dayPart.join(', ')}` : ''}${tail}`;
  }

  if (!has(minutes)) {
    return `Every minute in hours ${sorted(hours!).join(', ')}${dayPart.length ? ` on ${dayPart.join(', ')}` : ''}${tail}`;
  }
  const hStep = stepOf(hours!, 24);
  if (minutes!.length === 1 && hStep && !dayPart.length) {
    return `Every ${hStep} h at :${pad(minutes![0])}${tail}`;
  }
  const times = sorted(hours!).flatMap((h) => sorted(minutes!).map((m) => `${pad(h)}:${pad(m)}`));
  const shown =
    times.length > 6
      ? `${times.slice(0, 6).join(', ')} and ${times.length - 6} more`
      : times.join(', ');
  const lead = dayPart.length ? dayPart.join(', ') : 'Daily';
  return `${lead} at ${shown}${tail}`;
};

/* ---------- badges and notices ---------- */

const HEALTH_STYLE: Record<JobHealth, string> = {
  healthy: 'bg-green-50 text-green-700 ring-green-200',
  degraded: 'bg-amber-50 text-amber-700 ring-amber-200',
  failing: 'bg-red-50 text-red-700 ring-red-200',
  idle: 'bg-gray-100 text-gray-600 ring-gray-200',
  disabled: 'bg-slate-100 text-slate-500 ring-slate-200',
};

export const HealthBadge: React.FC<{ health?: JobHealth }> = ({ health = 'idle' }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
      HEALTH_STYLE[health] ?? HEALTH_STYLE.idle
    }`}
  >
    {health}
  </span>
);

export const OutcomeBadge: React.FC<{ code: number | string }> = ({ code }) => (
  <span
    className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
      isOk(code) ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
    }`}
  >
    {isOk(code) ? 'success' : `failed (${code})`}
  </span>
);

export const ACTION_LABEL: Record<AuditAction, string> = {
  run: 'Ran now',
  enable: 'Resumed',
  disable: 'Paused',
  abort: 'Aborted a run',
  ack_errors: 'Acknowledged failures',
  create: 'Created',
  update: 'Edited',
  delete: 'Deleted',
};

export const Card: React.FC<{
  title: string;
  children: React.ReactNode;
  note?: React.ReactNode;
  action?: React.ReactNode;
  flush?: boolean;
}> = ({ title, children, note, action, flush }) => (
  <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
    <div className="flex items-baseline justify-between gap-3 border-b border-gray-100 px-4 py-3">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      <div className="flex items-baseline gap-3">
        {note && <span className="text-xs text-gray-400">{note}</span>}
        {action}
      </div>
    </div>
    <div className={flush ? '' : 'p-4'}>{children}</div>
  </div>
);

export const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="min-w-0">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <div className="break-words text-sm text-gray-900">{children}</div>
  </div>
);

export const Th: React.FC<{ children?: React.ReactNode; right?: boolean }> = ({
  children,
  right,
}) => <th className={`px-4 py-2 font-medium ${right ? 'text-right' : ''}`}>{children}</th>;

export const EmptyRow: React.FC<{ cols: number; loading?: boolean; children: React.ReactNode }> = ({
  cols,
  loading,
  children,
}) => (
  <tr>
    <td colSpan={cols} className="px-4 py-8 text-center text-gray-400">
      {loading ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : children}
    </td>
  </tr>
);

/** Tells the reader when Cronicle kept less history than the window asks for. */
export const CoverageNote: React.FC<{ coverage?: Coverage }> = ({ coverage }) =>
  coverage && !coverage.complete ? (
    <p className="text-xs text-amber-700">
      History available from {coverage.from ? fmtTime(coverage.from) : 'an unknown date'}. Figures
      cover that period only.
    </p>
  ) : null;

export const WindowTabs = <W extends JobWindow = SchedulerWindow>({
  value,
  onChange,
  options,
}: {
  value: W;
  onChange: (w: W) => void;
  options?: W[];
}) => (
  <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
    {(options ?? (WINDOWS as W[])).map((w) => (
      <button
        key={w}
        type="button"
        onClick={() => onChange(w)}
        className={`px-3 py-1.5 text-xs font-medium ${
          value === w ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        {w}
      </button>
    ))}
  </div>
);

/* ---------- errors ---------- */

export const apiStatus = (error: unknown): number | undefined =>
  (error as { status?: number; statusCode?: number })?.status ??
  (error as { statusCode?: number })?.statusCode;

export const isNotConfigured = (error: unknown) =>
  apiStatus(error) === 503 && getApiErrorCode(error) === 'scheduler_not_configured';

const ERROR_COPY: Record<string, string> = {
  scheduler_unreachable: 'The scheduler could not be reached.',
  scheduler_timeout: 'The scheduler took too long to answer.',
  scheduler_key_rejected: 'The scheduler rejected the admin API key.',
  missing_reason: 'A reason is required.',
  invalid_fields: 'Some fields are not valid.',
};

/** One readable line for any scheduler API error. */
export const schedulerErrorText = (error: unknown): string => {
  const code = getApiErrorCode(error);
  const detail = getApiErrorField<string>(error, 'detail');
  const base = (code && ERROR_COPY[code]) || getApiErrorMessage(error, 'Something went wrong');
  return detail && typeof detail === 'string' ? `${base} ${detail}` : base;
};

export const NotConfigured: React.FC = () => (
  <div className="rounded-xl border border-amber-100 bg-amber-50 p-10 text-center shadow-sm">
    <p className="text-sm font-medium text-amber-900">Scheduler not connected</p>
    <p className="mt-1 text-sm text-amber-800">
      The scheduler is not connected yet. CRONICLE_API_KEY has not been set for the admin API.
    </p>
  </div>
);

export const ErrorNotice: React.FC<{ error: unknown; onRetry?: () => void }> = ({
  error,
  onRetry,
}) =>
  isNotConfigured(error) ? (
    <NotConfigured />
  ) : (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
      <span>{schedulerErrorText(error)}</span>
      {onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry}>
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          Retry
        </Button>
      )}
    </div>
  );

export const AccessDenied: React.FC<{ code?: string }> = ({ code = 'scheduler.view' }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
    <p className="text-sm font-medium text-gray-800">You don&apos;t have access to the scheduler</p>
    <p className="mt-1 text-sm text-gray-500">Ask an administrator for the {code} permission.</p>
  </div>
);

/* ---------- dialog ---------- */

/**
 * Confirm with a reason. `minReason` 0 makes the reason optional. `confirmText`
 * asks the user to type that exact text before the button enables.
 */
export const ReasonDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  minReason?: number;
  confirmText?: string;
  busy?: boolean;
  error?: unknown;
  onConfirm: (reason: string) => void;
}> = ({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  destructive,
  minReason = 0,
  confirmText,
  busy,
  error,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const reasonOk = reason.trim().length >= minReason;
  const typedOk = !confirmText || typed === confirmText;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          {confirmText && (
            <label className="block text-sm">
              <span className="text-gray-700">
                Type <span className="font-mono font-medium">{confirmText}</span> to confirm
              </span>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className={`${INPUT_CLASS} mt-1 w-full`}
              />
            </label>
          )}
          <label className="block text-sm">
            <span className="text-gray-700">
              Reason{minReason > 0 ? ` (at least ${minReason} characters)` : ' (optional)'}
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-blue-400"
            />
          </label>
          {!!error && <p className="text-sm text-red-600">{schedulerErrorText(error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={busy || !reasonOk || !typedOk}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Run now and Pause/Resume for one job; the caller decides whether to show it. */
export const JobOperateButtons: React.FC<{ job: SchedulerJob; size?: 'sm' | 'default' }> = ({
  job,
  size = 'sm',
}) => {
  const [dialog, setDialog] = useState<'run' | 'toggle' | null>(null);
  const run = useRunNowMutation();
  const toggle = useSetJobEnabledMutation();
  const enabled = job.enabled === 1;
  const close = () => {
    setDialog(null);
    run.reset();
    toggle.reset();
  };
  return (
    <>
      <Button size={size} variant="outline" onClick={() => setDialog('run')}>
        Run now
      </Button>
      <Button size={size} variant="outline" onClick={() => setDialog('toggle')}>
        {enabled ? 'Pause' : 'Resume'}
      </Button>
      {dialog === 'run' && (
        <ReasonDialog
          open
          onClose={close}
          title={`Run ${job.title} now`}
          description="Starts one run immediately, outside its schedule."
          confirmLabel="Run now"
          busy={run.isPending}
          error={run.error}
          onConfirm={(reason) =>
            run.mutate({ id: job.id, reason: reason || undefined }, { onSuccess: close })
          }
        />
      )}
      {dialog === 'toggle' && (
        <ReasonDialog
          open
          onClose={close}
          title={`${enabled ? 'Pause' : 'Resume'} ${job.title}`}
          description={
            enabled
              ? 'It will not start on its schedule until you resume it.'
              : 'It will start running on its schedule again.'
          }
          confirmLabel={enabled ? 'Pause' : 'Resume'}
          busy={toggle.isPending}
          error={toggle.error}
          onConfirm={(reason) =>
            toggle.mutate(
              { id: job.id, enabled: !enabled, reason: reason || undefined },
              { onSuccess: close },
            )
          }
        />
      )}
    </>
  );
};

export const JobLink: React.FC<{ id: string; title: string; className?: string }> = ({
  id,
  title,
  className = 'font-medium text-gray-900 hover:underline',
}) => (
  <Link to={`${SCHEDULER_BASE}/jobs/${encodeURIComponent(id)}`} className={className}>
    {title}
  </Link>
);

/* ---------- chart ---------- */

/** Runs per bucket, successes and failures stacked. Empty buckets stay on the axis. */
export const RunsChart: React.FC<{ series: SeriesPoint[]; window: JobWindow }> = ({
  series,
  window,
}) => {
  const data = series.map((p) => ({
    label:
      window === '24h'
        ? new Date(p.at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : window === '30d' || window === '90d'
          ? new Date(p.at * 1000).toLocaleDateString([], { month: 'short', day: 'numeric' })
          : new Date(p.at * 1000).toLocaleString([], {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
            }),
    Succeeded: Math.max(0, p.runs - p.failures),
    Failed: p.failures,
  }));
  if (!data.some((d) => d.Succeeded + d.Failed > 0)) {
    return <p className="py-10 text-center text-sm text-gray-400">No runs in this window.</p>;
  }
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          barCategoryGap="15%"
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            tick={{ fontSize: 11, fill: '#9ca3af' }}
          />
          <YAxis
            allowDecimals={false}
            width={32}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
          />
          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.03)' }}
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          <Bar dataKey="Succeeded" stackId="r" fill="#22c55e" />
          <Bar dataKey="Failed" stackId="r" fill="#ef4444" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
